<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script lang="ts">
import type { Component } from 'vue'

// Not great, not too bad either tbf...
export type Gap = 0 | 0.5 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16

export interface FlexProps {
  as?: string | Component
  asChild?: boolean
  direction: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  arrangement?: 'start' | 'end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'stretch'
  alignment?: 'normal' | 'start' | 'end' | 'center' | 'stretch'

  gap?: Gap | undefined
  rowGap?: Gap | undefined
  columnGap?: Gap | undefined
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed } from 'vue'

const {
  as = 'div',
  asChild = false,
  direction,
  arrangement = 'start',
  alignment = 'normal',
  gap = 4,
  rowGap = undefined,
  columnGap = undefined,
} = defineProps<FlexProps>()

defineSlots<{ default: () => unknown }>()

const withUnit = (v: number | undefined) => (typeof v !== 'undefined' ? `${v * 4}px` : v)

const gapUnit = computed(() => withUnit(gap))
const rowGapUnit = computed(() => withUnit(rowGap))
const columnGapUnit = computed(() => withUnit(columnGap))

const gapStyle = computed(() => {
  if (gapUnit.value) {
    return {
      gap:
        rowGapUnit.value || columnGapUnit.value
          ? `${rowGapUnit.value ?? gapUnit.value} ${columnGapUnit.value ?? gapUnit.value}`
          : gapUnit.value,
    }
  }

  if (rowGapUnit.value && columnGapUnit.value) {
    return { gap: `${rowGapUnit.value} ${columnGapUnit.value}` }
  }

  if (rowGapUnit.value) {
    return { rowGap: rowGapUnit.value }
  }

  if (columnGapUnit.value) {
    return { columnGap: columnGapUnit.value }
  }

  return undefined
})
</script>

<template>
  <Primitive
    :as="as"
    :as-child="asChild"
    :class="[
      'flopui-flex',
      `flex-${direction}`,
      `flex-justify-${arrangement}`,
      alignment !== 'normal' && `flex-align-${alignment}`,
    ]"
    :style="gapStyle"
  >
    <slot />
  </Primitive>
</template>

<style>
.flopui-flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-row-reverse {
  flex-direction: row-reverse;
}

.flex-column {
  flex-direction: column;
}

.flex-column-reverse {
  flex-direction: column-reverse;
}

.flex-justify-start {
  justify-content: flex-start;
}

.flex-justify-end {
  justify-content: flex-end;
}

.flex-justify-center {
  justify-content: center;
}

.flex-justify-space-between {
  justify-content: space-between;
}

.flex-justify-space-around {
  justify-content: space-around;
}

.flex-justify-space-evenly {
  justify-content: space-evenly;
}

.flex-justify-stretch {
  justify-content: stretch;
}

.flex-align-start {
  align-items: flex-start;
}

.flex-align-end {
  align-items: flex-end;
}

.flex-align-center {
  align-items: center;
}
</style>
